import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { JsonHttpService } from 'src/app/shared/http/json-http.service';
import { AllSettingsGrid, SaveSettingsRow } from '../models/all-settings-grid';
import { firstValueFrom } from 'rxjs';
import { MailSettings } from '../models/mail-settings';
import { ProductFruitsConfig } from '../models/product-fruits-config';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  private http = inject(JsonHttpService);

  getProductFruitsConfig(): Promise<ProductFruitsConfig> {
    return firstValueFrom(this.http.getTyped<ProductFruitsConfig>('/api/configuration/get-productfruits-config'));
  }

  getEnvironmentColor(): Promise<string> {
    return firstValueFrom(this.http.getTyped<string>('/api/configuration/get-environment-color'));
  }

  getActiveEnvironment(): Promise<string> {
    return firstValueFrom(this.http.getTyped<string>('/api/configuration/get-current-environment-type'));
  }

  getMailSettings(): Promise<MailSettings> {
    return firstValueFrom(this.http.getTyped<MailSettings>('/api/configuration/get-mail-settings'));
  }

  saveMailSettings(mailSettings: MailSettings): Promise<MailSettings> {
    return firstValueFrom(this.http.postTyped<MailSettings>('/api/configuration/save-mail-settings', mailSettings));
  }

  getEffectiveSettingValue(key: string): Promise<string> {
    const options = { params: new HttpParams().set('key', key) };
    return firstValueFrom(this.http.getTyped<string>('/api/configuration/get-effective-setting-value', options));
  }

  saveEffectiveSettingValue(key: string, value: string): Promise<string> {
    const options = { params: new HttpParams().set('key', key) };
    return firstValueFrom(this.http.post('/api/configuration/save-effective-setting-value', JSON.stringify(value), options));
  }

  async getEffectiveSettingBoolean(key: string): Promise<boolean> {
    const result = await this.getEffectiveSettingValue(key);
    return result && (result.toLowerCase() !== 'false');
  }

  async saveEffectiveSettingBoolean(key: string, value: boolean): Promise<boolean> {
    const result = await this.saveEffectiveSettingValue(key, value ? 'True' : 'False');
    return result && (result.toLowerCase() !== 'false');
  }

  getAllSettings(): Promise<AllSettingsGrid> {
    return firstValueFrom(this.http.getTyped<AllSettingsGrid>('/api/configuration/get-all-settings'));
  }

  saveSettingsRow(row: SaveSettingsRow, useGenericErrorHandling: boolean = true): Promise<SaveSettingsRow> {
    return firstValueFrom(this.http.post('/api/configuration/save-settings', row, null, useGenericErrorHandling));
  }

  deleteAllSettingsRow(id: number, useGenericErrorHandling: boolean = true): Promise<void> {
    const options = { params: new HttpParams().set('settingsId', id.toString()) };
    return firstValueFrom(this.http.delete('/api/configuration/delete-setting', options, useGenericErrorHandling));
  }
}
